import type { LangCode } from '@sm/i18n';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { applyLang, applyTheme, currentTheme, type ThemeName } from '@/lib/preferences';
import { qk, useUpdateMe } from '@/lib/queries';

/**
 * Tema: lokalno (`sm-theme`) + PATCH /me kad je korisnik prijavljen (`persistRemote`).
 */
export function useTheme(persistRemote = false) {
  const [theme, setTheme] = useState<ThemeName>(() => currentTheme());
  const update = useUpdateMe();
  const toggle = useCallback(() => {
    const next: ThemeName = currentTheme() === 'dark' ? 'light' : 'dark';
    applyTheme(next);
    setTheme(next);
    if (persistRemote) update.mutate({ theme: next });
  }, [persistRemote, update]);
  return { theme, toggle };
}

export function useLanguage(persistRemote = false) {
  const { i18n } = useTranslation();
  const qc = useQueryClient();
  const update = useUpdateMe();
  const lang = (i18n.resolvedLanguage ?? i18n.language ?? 'sr') as LangCode;
  const change = useCallback(
    (code: LangCode) => {
      if (code === lang) return;
      if (!applyLang(code)) return;
      if (persistRemote) {
        update.mutate({ language: code });
        // Serverski prevedeni sadržaji (obavještenja) se ponovo učitavaju na novom jeziku.
        void qc.invalidateQueries({ predicate: (q) => q.queryKey[0] !== qk.me[0] });
      }
    },
    [lang, persistRemote, update, qc],
  );
  return { lang, change };
}
